import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Brain, ClipboardList } from "lucide-react";
import { SelfReportForm } from "@/components/PostMatchReport";

const Stat = ({ label, value, suffix, color, testid }) => (
  <div className="border border-pb-border bg-pb-elevated/40 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`font-display font-black text-2xl ${color}`}>{value ?? "—"}<span className="text-xs text-zinc-500 font-mono ml-1">{value != null ? suffix : ""}</span></div>
  </div>
);

export default function SelfReportInsights() {
  const [d, setD] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const load = () => api.get("/player/self-reports/insights").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const pending = d.pending || [];

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="lab-self-report">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold uppercase text-lg flex items-center gap-2"><Brain className="w-4 h-4 text-cyan-400" /> Post-Match Self Reports</h2>
          <p className="text-xs text-zinc-500">Fatigue · confidence · struggle areas after every match — the PBPA engine uses these to find what's really costing you points.</p>
        </div>
        {pending.length > 0 && (
          <button data-testid="lab-self-report-log-btn" onClick={() => setShowLog((v) => !v)}
            className="border border-cyan-400/60 text-cyan-300 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 hover:bg-cyan-400/10 transition-colors flex items-center gap-1.5">
            <ClipboardList className="w-3.5 h-3.5" /> {pending.length} match{pending.length > 1 ? "es" : ""} to report
          </button>
        )}
      </div>

      {showLog && pending[0] && (
        <div className="border border-cyan-400/40 bg-cyan-400/[0.04] p-4 mb-4" data-testid="lab-self-report-inline">
          <SelfReportForm match={pending[0]} areas={d.areas || []} onDone={() => load()} />
        </div>
      )}

      {d.count === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="lab-self-report-empty">No reports yet — after your next tournament, league or rec match a 30-second check-in will pop up. Log a few and patterns show up here.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat label="Reports" value={d.count} suffix="matches" color="text-white" testid="lab-sr-count" />
            <Stat label="Avg Fatigue" value={d.avg_fatigue} suffix="/ 5" color="text-amber-400" testid="lab-sr-fatigue" />
            <Stat label="Avg Confidence" value={d.avg_confidence} suffix="/ 5" color="text-cyan-300" testid="lab-sr-confidence" />
          </div>
          {d.top_struggles.length > 0 && (
            <div className="mb-4" data-testid="lab-sr-struggles">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Where matches get away from you</div>
              <div className="space-y-1.5">
                {d.top_struggles.map((s, i) => (
                  <div key={s.key} data-testid={`lab-sr-struggle-${s.key}`}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className={i === 0 ? "text-pb-red font-bold" : "text-zinc-300"}>{s.label}{i === 0 ? " · primary weakness" : ""}</span>
                      <span className="font-mono text-zinc-500">{s.count}× · {s.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-pb-elevated border border-pb-border/50"><div className="h-full transition-all duration-700" style={{ width: `${s.pct}%`, background: i === 0 ? "#E10600" : "#F59E0B" }} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.patterns.length > 0 && (
            <div className="space-y-1.5 mb-4" data-testid="lab-sr-patterns">
              {d.patterns.map((p, i) => (
                <div key={i} className="border-l-2 border-cyan-400 pl-3 text-xs text-zinc-200" data-testid={`lab-sr-pattern-${p.kind}`}>🧠 {p.text}</div>
              ))}
            </div>
          )}
          {d.recommendations.length > 0 && (
            <div className="border-t border-pb-border pt-3 space-y-1" data-testid="lab-sr-recs">
              {d.recommendations.map((r, i) => (
                <div key={i} className="text-xs text-cyan-300">{r.type === "drill" ? "⚗️" : "💡"} {r.text}</div>
              ))}
            </div>
          )}
          {d.recent.length > 0 && (
            <details className="mt-4" data-testid="lab-sr-recent">
              <summary className="text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-white">Recent reports ({d.recent.length})</summary>
              <div className="mt-2 space-y-1">
                {d.recent.map((r) => (
                  <div key={r.ref} className="flex flex-wrap items-center gap-x-3 border-t border-pb-border/50 py-1.5 text-[11px]">
                    <span className={`font-mono font-bold ${r.won ? "text-emerald-300" : r.won === false ? "text-pb-red" : "text-zinc-400"}`}>{r.won ? "W" : r.won === false ? "L" : "—"}</span>
                    <span className="text-zinc-300">{r.opp ? `vs ${r.opp}` : r.where}</span>
                    <span className="text-amber-400">fatigue {r.fatigue}</span>
                    <span className="text-cyan-300">conf {r.confidence}</span>
                    {r.struggles.length > 0 && <span className="text-zinc-500">{r.struggles.join(", ")}</span>}
                    {r.note && <span className="text-zinc-400 italic">"{r.note}"</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
